/* @flow */
import { pickBy, has } from 'lodash';

import apiRequest from 'lib/apiRequest';
import Entity from 'lib/Entity';
import File from 'entities/File';
import MessageRecipients from 'collections/MessageRecipients';
import MessageSender from 'entities/MessageSender';
import { prefillMessageWithGroupName } from 'utils/prefillMessageWithGroupName';
import Activity from 'entities/Activity';
import ReactionSummary from 'entities/ReactionSummary';
import uuid from 'utils/uuid';

export const TARGET_TYPES = {
  GROUP_TARGET: 'group_target',
  INDIVIDUAL_TARGET: 'individual_target',
};

export type GroupTargetType = 'group_target';
export type IndividualTargetType = 'individual_target';

export type GroupTarget = {
  type: GroupTargetType,
  role: string,
  group_id?: number,
  name?: string,
  uuid?: string,
  preview?: ?{
    preview: string,
    sent_at: any,
  },
  count?: number,
};

export type IndividualTarget = {
  type: IndividualTargetType,
  member_id: number,
};

export type Target = GroupTarget | IndividualTarget;

/**
 * Announcement, really. (This was named before we had chat.)
 */
export class Message extends Entity {
  urlRoot() {
    return '/messages';
  }

  defaults() {
    return {
      attachmentId: null,
      body: '',
      created_at: new Date(),
      files: [],
      group_ids: null,
      params: {},
      payment_req: null,
      received_count: null,
      recipients: [],
      send_at: null,
      sent_at: null,
      status: null,
      uuid: uuid(),
      is_client_created: false,
    };
  }

  parse(response: Object) {
    const data = Object.assign({}, response);

    if (data['created_at']) {
      data['created_at'] = new Date(data['created_at']);
    }

    if (data['send_at']) {
      data['send_at'] = new Date(data['send_at']);
    }

    if (data['sent_at']) {
      data['sent_at'] = new Date(data['sent_at']);
    }

    if (data['sender']) {
      data['sender'] = new MessageSender(data['sender']);
    }

    if (data['files']) {
      data['files'] = data['files'].map(file => File.fromJSON(file));
    }

    if (data['recipients']) {
      data['recipients'] = MessageRecipients.fromJSON(data['recipients']);
    }

    if (data['payment_req']) {
      data['payment_req'] = Activity.fromJSON(data['payment_req']);
    }

    if (data['reaction_summary']) {
      data['reaction_summary'] =
        ReactionSummary.fromJSON(data['reaction_summary']);
    }

    return data;
  }

  setReactionSummary(reactionSummary: ReactionSummary) {
    this.set('reaction_summary', reactionSummary);
  }

  getReactionSummary(): ReactionSummary {
    return this.get('reaction_summary') || new ReactionSummary();
  }

  getSeq() {
    return this.get('seq');
  }

  getType(): string {
    return this.get('type');
  }

  getAttachmentId() {
    return this.get('attachmentId');
  }

  getISO639Locale(): ?string {
    let locale = this.get('locale');

    if (locale) {
      locale = locale.split('-')[0];
    }

    return locale;
  }

  getBody(): ?string {
    return this.get('body');
  }

  getSender(): ?MessageSender {
    return this.get('sender');
  }

  getCreatedAt() {
    return this.get('created_at');
  }

  getSendAt() {
    return this.get('send_at');
  }

  getSentTo() {
    return this.get('sent_to');
  }

  getSentAt() {
    return (this.getStatus() === 'pending') ? this.getSendAt() : this.get('sent_at');
  }

  getStatus() {
    return this.get('status');
  }

  // $FlowFixMe
  setStatus(status) {
    return this.set({ status });
  }

  getRecipients() {
    return this.get('recipients');
  }

  getGroupIds() {
    return this.get('group_ids') || this.getGroupIdsFromRecipients();
  }

  getGroupIdsFromRecipients() {
    return this.getRecipients()
      .filter(recipient => recipient.isGroup())
      .map(recipient => recipient.id);
  }

  getReceivedCount() {
    return this.get('received_count');
  }

  getFiles(): File[] {
    return this.get('files');
  }

  getFile(): ?File {
    const files = this.getFiles();
    return (files.length > 0) ? files[0] : null;
  }

  getGapParams() {
    return this.get('params');
  }

  getActivity() {
    return this.get('payment_req');
  }

  getPreview() {
    const preview = (
      this.getBody() ||
      (this.getFile() && '1 Attachment 📚') ||
      '<error>'
    );
    return `Announcement: ${preview}`;
  }

  isWorthSaving(groupName: string) {
    return (this.hasBody() || this.getFiles().length > 0)
     && this.getBody() !== prefillMessageWithGroupName(groupName);
  }

  hasBody(): bool {
    const body = this.getBody();
    if (body == null) {
      return false;
    }
    return (body.trim().length > 0);
  }

  getThreads(): Target[] {
    let result = [];
    const threads = this.get('threads');
    if (threads) {
      result = threads.map((t) => {
        const type = has(t, 'group_id') ?
          TARGET_TYPES.GROUP_TARGET :
          TARGET_TYPES.INDIVIDUAL_TARGET;
        return { type, ...t };
      });
    }
    return result;
  }

  getUuid() {
    return this.get('uuid');
  }

  getReadSummary() {
    return this.get('read_summary');
  }

  getTargets(): Target[] {
    let result = [];
    const targets = this.get('targets');
    if (targets) {
      result = targets.map((t) => {
        const type = has(t, 'group_id') ?
          TARGET_TYPES.GROUP_TARGET :
          TARGET_TYPES.INDIVIDUAL_TARGET;
        return { type, ...t };
      });
    }
    return result;
  }

  getRoles() {
    // $FlowFixMe
    const targets = this.getTargets().filter(t => t.role).map(t => t.role);
    return targets.length > 0 ? targets :
      // $FlowFixMe
      this.getThreads().filter(t => t.role).map(t => t.role);
  }

  getIsClientCreated(): bool {
    return this.get('is_client_created');
  }

  sentByCurrentUser() {
    // XXX remove CurrentUser from entities
    const CurrentUser = require('auth/CurrentUser').default;
    const sender = this.getSender();
    return sender && sender.id === CurrentUser.getUserId();
  }

  // Used when adding file by file ID
  setOnlyFile(file: Object) {
    return this.set('files', [file]);
  }

  send() {
    if (!this.isNew()) {
      // The message has already been sent.
      return new Promise(resolve => resolve(this));
    }
    const targets = this.getTargets();
    let message = {
      body: this.getBody(),
      group_ids: targets.length ? null : this.getGroupIds(),
      send_at: this.getSendAt(),
      files: this.getFiles(),
      uuid: this.getUuid(),
      payment_req_uuid: this.get('payment_req_uuid'),
      is_client_created: true,
      targets,
    };

    // remove any undefined or null options
    message = pickBy(message, (v: any): boolean => (typeof v !== 'undefined') && (v !== null));

    return this.save({ sent_at: new Date() }, {
      data: {
        message,
      },
    });
  }

  static findByUuid(messageUuid) {
    return apiRequest(`/messages/${messageUuid}`, 'GET').then((messageJSON) => {
      return Message.fromJSON(messageJSON);
    });
  }

  static createMessage(groups, text, sendAt, files, sender, sentAt, targets) {
    // XXX remove CurrentUser from entities
    const CurrentUser = require('auth/CurrentUser').default;
    const user = CurrentUser.isSignedIn() ? CurrentUser.getUser() : {};

    const filesAreEntities = !!(files && files.length && files[0].attributes);

    let options = {
      group_ids: groups ? groups.map(group => group.id) : null,
      recipients: groups ? groups.map(group => {
        return {
          id: group.id,
          name: group.getName(),
          type: 'group',
        };
      }) : null,
      send_at: sendAt,
      sent_at: sentAt,
      body: text,
      sender: sender || { id: user.id, name: user.getSignature() },
      files: filesAreEntities ? files.map(f => f.toJSON()) : files,
      targets,
    };

    // remove any undefined or null options
    options = pickBy(options, (v: any): boolean => (typeof v !== 'undefined') && (v !== null));
    return Message.fromJSON(options);
  }

  /**
   * @return {Message} a version of this message suitable to send again
   */
  reuse() {
    const sendable = this.clone();
    sendable.set({
      status: null,
      send_at: null,
      sent_at: null,
      uuid: uuid(),
    });
    return sendable;
  }

  // This should not update 'files' because the urls
  // aren't ready until returned by the server in patch
  // $FlowFixMe
  update(body, targets, sendAt) {
    this.set({
      body,
      targets,
      send_at: sendAt,
    });

    const changes = this.changedAttributes() || {};

    // The message was changed to unscheduled.
    // Placed below changes because it's only
    // meant for view purposes
    if (!sendAt) {
      this.set({
        status: null,
        sent_at: new Date(),
      });
    }

    return changes;
  }

  // $FlowFixMe
  patch(changes, files) {
    const message = Object.assign(changes, { files });

    return this.save({ message }, { patch: true });
  }

  toJSON(): Object {
    return this.recursivelyToJSON();
  }
}

export class GapMessage extends Message {
  getType() {
    return 'gap';
  }

  getUuid() {
    return 'gap-uuid';
  }
}

export default Message;



// WEBPACK FOOTER //
// ./src/entities/Message.js